/**
 * Date handling for the BE's two date-filter conventions (FE plan §3.8):
 *
 *   'profile-tz'  `startDate` / `endDate` — YYYY-MM-DD, interpreted in the
 *                 ADMIN PROFILE's timezone; `endDate` includes the whole day.
 *                 (profiles, analytics, feedback, campaigns, listings)
 *   'utc'         `from` / `to` — YYYY-MM-DD, UTC, HALF-OPEN: `to` is
 *                 exclusive and must be sent together with `from`.
 *                 (alerts, reports, recall)
 *
 * The UI always works in inclusive calendar days; `toApiRange` converts.
 */

export type DateConvention = 'profile-tz' | 'utc';

/** An inclusive range of calendar days, YYYY-MM-DD. */
export interface DayRange {
  start?: string;
  end?: string;
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isDay(value: unknown): value is string {
  return typeof value === 'string' && DAY_RE.test(value);
}

/** Adds `days` to a YYYY-MM-DD, calendar-wise (no timezone involved). */
export function addDays(day: string, days: number): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

/** Today as YYYY-MM-DD in the given IANA timezone (defaults to the browser's). */
export function today(timeZone?: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(
    new Date(),
  );
}

/** Query params for an inclusive day range under the endpoint's convention. */
export function toApiRange(range: DayRange, convention: DateConvention): Record<string, string | undefined> {
  if (convention === 'profile-tz') {
    return { startDate: range.start, endDate: range.end };
  }
  // UTC half-open: both or neither, and `to` is the day AFTER the last day.
  if (!range.start || !range.end) return { from: undefined, to: undefined };
  return { from: range.start, to: addDays(range.end, 1) };
}

/** The kit's range chips: trailing N days ending today, or no bound ("All"). */
export type RangePreset = '7d' | '30d' | '90d' | 'all';

export function presetRange(preset: RangePreset, timeZone?: string): DayRange {
  if (preset === 'all') return {};
  const days = { '7d': 7, '30d': 30, '90d': 90 }[preset];
  const end = today(timeZone);
  return { start: addDays(end, -(days - 1)), end };
}

/**
 * Calendar periods (the range control's calendar popover). Quarters are
 * CALENDAR quarters: Jan–Mar, Apr–Jun, Jul–Sep, Oct–Dec. Every range is
 * inclusive days in the given timezone; "to date" periods end today.
 */
export type CalendarPeriod =
  'last-30' | 'last-12mo' | 'mtd' | 'last-month' | 'qtd' | 'last-quarter' | 'ytd' | 'last-year';

export const CALENDAR_PERIODS: Array<[CalendarPeriod, string]> = [
  ['last-30', 'Last 30 days'],
  ['last-12mo', 'Last 12 months'],
  ['mtd', 'Month to date'],
  ['last-month', 'Last month'],
  ['qtd', 'Quarter to date'],
  ['last-quarter', 'Last quarter'],
  ['ytd', 'Year to date'],
  ['last-year', 'Last year'],
];

function ymd(y: number, m: number, d: number): string {
  // Date.UTC normalises overflow (month 13, day 0), which the month maths relies on.
  return new Date(Date.UTC(y, m - 1, d)).toISOString().slice(0, 10);
}

export function calendarRange(period: CalendarPeriod, timeZone?: string): DayRange {
  const end = today(timeZone);
  const [y, m] = end.split('-').map(Number) as [number, number];
  const qStart = Math.floor((m - 1) / 3) * 3 + 1; // first month of this quarter
  switch (period) {
    case 'last-30':
      return { start: addDays(end, -29), end };
    case 'last-12mo':
      // Rolling twelve months ending today: the day after this date last year.
      return { start: addDays(ymd(y - 1, m, Number(end.slice(8))), 1), end };
    case 'mtd':
      return { start: ymd(y, m, 1), end };
    case 'last-month':
      return { start: ymd(y, m - 1, 1), end: ymd(y, m, 0) };
    case 'qtd':
      return { start: ymd(y, qStart, 1), end };
    case 'last-quarter':
      return { start: ymd(y, qStart - 3, 1), end: ymd(y, qStart, 0) };
    case 'ytd':
      return { start: ymd(y, 1, 1), end };
    case 'last-year':
      return { start: ymd(y - 1, 1, 1), end: ymd(y - 1, 12, 31) };
  }
}

/**
 * A custom range, as the range control stores it in the URL:
 * `YYYY-MM-DD..YYYY-MM-DD`, inclusive, start on or before end.
 */
export type CustomRange = `${string}..${string}`;

export function isCustomRange(value: unknown): value is CustomRange {
  if (typeof value !== 'string') return false;
  const [start, end, extra] = value.split('..');
  return extra === undefined && isDay(start) && isDay(end) && start <= end;
}

export function customRange(start: string, end: string): CustomRange {
  return `${start}..${end}`;
}

export function customRangeDays(value: CustomRange): DayRange {
  const [start, end] = value.split('..') as [string, string];
  return { start, end };
}

/** YYYY-MM-DD → "09/20/26". */
export function shortDay(day: string): string {
  const [y, m, d] = day.split('-');
  return `${m}/${d}/${y?.slice(2)}`;
}

/**
 * A typed day → YYYY-MM-DD, or undefined. Accepts M/D/YY, MM/DD/YY and
 * MM/DD/YYYY (two-digit years are 20xx), and YYYY-MM-DD; rejects impossible
 * dates such as 02/30/26.
 */
export function parseTypedDay(text: string): string | undefined {
  const t = text.trim();
  if (isDay(t)) return validDay(t);
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/.exec(t);
  if (!m) return undefined;
  const year = m[3]!.length === 2 ? 2000 + Number(m[3]) : Number(m[3]);
  return validDay(`${year}-${m[1]!.padStart(2, '0')}-${m[2]!.padStart(2, '0')}`);
}

function validDay(day: string): string | undefined {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return ymd(y, m, d) === day ? day : undefined;
}

const dateFmt = new Map<string, Intl.DateTimeFormat>();

/** "Sep 26, 2026" in the given timezone. ISO string or Date. */
export function formatDate(value: string | Date | null | undefined, timeZone?: string): string {
  if (!value) return '—';
  const key = `d:${timeZone ?? ''}`;
  let fmt = dateFmt.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', { timeZone, month: 'short', day: 'numeric', year: 'numeric' });
    dateFmt.set(key, fmt);
  }
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : fmt.format(date);
}

/** "Sep 26, 2026, 7:40 AM" in the given timezone. */
export function formatDateTime(value: string | Date | null | undefined, timeZone?: string): string {
  if (!value) return '—';
  const key = `dt:${timeZone ?? ''}`;
  let fmt = dateFmt.get(key);
  if (!fmt) {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
    dateFmt.set(key, fmt);
  }
  const date = typeof value === 'string' ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? '—' : fmt.format(date);
}
